/**
 * Maturity ladder definitions for the task generation model.
 *
 * ## Domain model: Step → Ladder → Rung → Closure Criteria (DoD)
 *
 * - Step = a domain pillar on the canvas (Customer, Product Build, GTM,
 *   etc.). There are 8 step IDs in `src/constants/steps.ts`.
 * - Ladder = a maturity progression attached to exactly one step. 7 of the
 *   8 steps have one (Idea doesn't). Defined in `src/constants/ladders.ts`.
 * - Rung = a maturity level within a ladder. Each ladder has 3 rungs at
 *   levels [-1, 0, 1] — roughly "hypothesizing → validated → creation."
 *   Each rung has a `name` (like "Validated") and optionally a `closureDoD`
 *   — the checklist that must be satisfied before advancing.
 *
 * "Sits at a rung" means the project's progress pointer for that ladder
 * points to rung level N (stored in `Project.ladderRungs` as a JSON map of
 * `ladderId → level`).
 *
 * One ladder per domain (7 total). Universal 3-rung shape across all ladders:
 *   R−1 — Ideated. Lev's canvas setup. Auto-completed by Lev's onboarding
 *         outputs. The first auto-eval fires when the idea block lands (see
 *         `onProjectContentChanged.ts`).
 *   R0  — Validated. Founder works toward the per-step eval grade target.
 *         Process DoD = recommended path (not a gate). Eval iteration tasks
 *         drive the grade up after each cycle.
 *   R1  — Creation. Task model deferred — out of scope for the current
 *         playbook. The bridge advances founders here when R0 grade clears;
 *         no tasks are generated at R1 yet.
 *
 * Product Build collapses its previous 5-rung shape into the same universal
 * R−1 → R0 → R1 sequence. Legacy R2 "Defined" maps to new R0; legacy R3-R5
 * (Building / Testing / Iterating) fold into R1 Creation. PB R0's DoD varies
 * by `Project.rubricType` (venture vs. bootstrap), so its `closureDoD` is a
 * rubric-conditional map (Option A) rather than a flat array.
 *
 * `gradeAnchors` live on R0 for every anchored ladder. Brand R0 + Team R0
 * stay anchorless (process-only, LLM-judged). Grade targets per the locked
 * R0 venture-task-model table:
 *   CD R0 → Customer Problem ≥ B
 *   Comp R0 → Competition & Moat ≥ B
 *   Market R0 → TAM & Timing ≥ B AND Business Model ≥ B (multi-anchor)
 *   GTM R0 → Go-To-Market ≥ A
 *   PB R0 venture → Product Feasibility ≥ A
 *   PB R0 bootstrap → Speed of product build ≥ B AND Easy onboarding ≥ B
 *
 * Source of truth: src/constants/ladders.ts
 */
import { STEP_IDS, type StepId } from "./steps";

export const LADDER_IDS = {
  CUSTOMER_DISCOVERY: "customer-discovery",
  COMPETITIVE_LANDSCAPING: "competitive-landscaping",
  MARKET_SIZING_PRICING: "market-sizing-pricing",
  BRAND_DEVELOPMENT: "brand-development",
  GO_TO_MARKET: "go-to-market",
  PRODUCT_BUILD: "product-build",
  TEAM: "team",
} as const;

export type LadderId = (typeof LADDER_IDS)[keyof typeof LADDER_IDS];

export type SignalType = "artifact" | "activity" | "insight" | "mixed";
export type ClosureType = "auto" | "soft" | "hard";

/** Project rubric variants — drives PB R0 DoD selection + grade-anchor selection. */
export type RubricType = "venture" | "bootstrap";

export type Grade = "A" | "B" | "C" | "D";

export interface DoDCriterion {
  /**
   * Stable, kebab-case identifier for this criterion. Format:
   * `r<level>-<ladder-slug>[-<rubric>]-<short-slug>` (e.g.
   * `r0-customer-discovery-interviews-5`, `r0-product-build-venture-prd-spec`).
   * Used as the JOIN KEY for `processVerdicts` and any other programmatic
   * cross-reference. NEVER use `description` for joins — description text is
   * editable and the founder-facing wording drifts over time. The id is the
   * identity. Must be globally unique across every ladder, every rung, every
   * rubric variant.
   */
  id: string;
  signal: SignalType;
  description: string;
}

/** Per-rung eval gate descriptor. */
export interface GradeAnchor {
  /** Rubric category name. Exact match to ProjectEvaluation.structuredData.criteriaGrades[].name. */
  category: string;
  /** Which rubric variant this anchor applies to. */
  rubric: RubricType | "both";
  /** Minimum letter grade required. Per-step targets: CD/Comp/Market/PB-bootstrap = B; GTM/PB-venture = A. */
  minGrade: Grade;
}

export interface Rung {
  level: number;
  name: string;
  description: string;
  /**
   * Criteria that close this rung. Most ladders use the flat array form.
   * PB R0 uses the rubric-conditional map (Option A) because venture and
   * bootstrap variants differ; consumers MUST resolve the variant for the
   * project's `rubricType` before reading.
   *
   * R−1 AUTO rungs and R1 placeholder rungs have no closure DoD.
   */
  closureDoD?: DoDCriterion[] | Partial<Record<RubricType, DoDCriterion[]>>;
  closureType?: ClosureType;
  /**
   * Tasks at this rung surface real-world signal (interviews, tests, experiments).
   * Negative insights captured on test-rung tasks can trigger iteration on the same rung.
   */
  isTestRung?: boolean;
  /**
   * Rubric category(s) + min grade(s) that gate this rung's closure. Lives on
   * R0 for every anchored ladder. Bridge advances R0 → R1 when ALL anchors
   * matching the project's `rubricType` reach their target grade.
   */
  gradeAnchors?: GradeAnchor[];
}

/**
 * Marker the bootstrap and migration script set on `Project.ladderRungs` once
 * the project has been written into the current rung shape. Re-running the
 * migration short-circuits when this is present at the current version, so
 * already-migrated values can't be remapped a second time. Consumers ignore
 * non-LadderId keys when reading `ladderRungs`, so this key is invisible to
 * runtime task generation.
 *
 * Version history:
 *   1 — pre-PR1 (legacy 0-indexed shape)
 *   2 — PR1 universal 3-rung shape (R1=auto, R2=process, R3=eval test)
 *   3 — PR3 universal R−1 / R0 / R1 collapse (current)
 */
export const RUNG_SHAPE_VERSION_KEY = "__tasksV3RungShape" as const;
export const TASKS_V3_RUNG_SHAPE_VERSION = 3 as const;

/**
 * In-JSON marker stamped inside `Project.ladderRungs` recording which rubric
 * the active rung map currently reflects. Mirrors `RUNG_SHAPE_VERSION_KEY`.
 *
 * The seed builds `ladderRungs` for the project's rubric at seed time, but
 * `Project.rubricType` is mutable afterward (re-evaluation). The orchestrator
 * compares this marker against the live `rubricType` and, on mismatch, swaps
 * the active map with the snapshot held in `Project.ladderRungsByRubric` so
 * each rubric's rung progress is tracked independently and never mixed.
 *
 * `__`-prefixed so readers iterating `ALL_LADDERS` ignore it, exactly like the
 * shape-version marker.
 */
export const ACTIVE_RUNGS_RUBRIC_KEY = "__rungsRubric" as const;

export interface Ladder {
  id: LadderId;
  name: string;
  stepId: StepId;
  /**
   * Highest rung the system will seed from onboarding + canvas-setup state
   * alone. R−1 is Lev's auto-complete state across every ladder.
   */
  onboardingCapRung: number;
  rungs: Rung[];
  /**
   * Rubric variants under which this ladder should be skipped entirely.
   * "Skipped" means new projects seed the ladder one rung past R0 (i.e.
   * straight into R1 Creation), so no tasks generate, no eval fires, no
   * grade anchor applies, and the ladder is effectively graduated from
   * day 1. Used to omit ladders that don't apply under a given rubric —
   * e.g. Competitive Landscaping + Team are skipped under the bootstrap
   * rubric (decided 2026-06-14).
   *
   * Venture-only ladders never set this. Resolving an empty / undefined
   * value yields the normal R0 seed behavior across all rubrics.
   */
  skipForRubric?: readonly RubricType[];
}

// ---------------------------------------------------------------------------
// Customer Discovery (R0 DoD is rubric-conditional)
// ---------------------------------------------------------------------------

const CD_R0_VENTURE_DOD: DoDCriterion[] = [
  // CD is the count-exception: ≥5 conversations stays.
  {
    id: "r0-customer-discovery-venture-conversations-5",
    signal: "activity",
    description: "≥5 customer conversations documented",
  },
  {
    id: "r0-customer-discovery-venture-pain-verbatim",
    signal: "mixed",
    description:
      "Pain described in customers' own words, with verbatim quotes — leading to evidence of pain point",
  },
  {
    id: "r0-customer-discovery-venture-motivation-evidence",
    signal: "insight",
    description:
      "Motivation evidence: customer describes attempted workarounds, hacks, or current coping for the pain (not just \"yes, I have this problem\")",
  },
  {
    id: "r0-customer-discovery-venture-solution-fit",
    signal: "insight",
    description:
      "Solution-fit checked from customer POV (does the proposed solution address root cause for them?)",
  },
];

const CD_R0_BOOTSTRAP_DOD: DoDCriterion[] = [
  {
    id: "r0-customer-discovery-bootstrap-smoke-test-live",
    signal: "artifact",
    description:
      "Smoke test live in 3–7 days: static LP + working demo + waitlist/checkout, deployed and reachable from a paid-search ad",
  },
  {
    id: "r0-customer-discovery-bootstrap-funnel-measurable",
    signal: "mixed",
    description:
      "Measurable funnel from cold search traffic: CTR → LP CVR → trial start → checkout intent, each step instrumented",
  },
  {
    id: "r0-customer-discovery-bootstrap-decision-thresholds",
    signal: "artifact",
    description:
      "Pre-declared decision thresholds written down (e.g. \"build if >10 paid trials from 300 clicks\")",
  },
  {
    id: "r0-customer-discovery-bootstrap-decision-recorded",
    signal: "insight",
    description:
      "Decision recorded against the pre-declared thresholds (build / pivot / kill) with the funnel data that drove it",
  },
];

const CUSTOMER_DISCOVERY: Ladder = {
  id: LADDER_IDS.CUSTOMER_DISCOVERY,
  name: "Customer Discovery",
  stepId: STEP_IDS.CUSTOMER_PROBLEM,
  onboardingCapRung: -1,
  rungs: [
    {
      level: -1,
      name: "Hypothesizing",
      description:
        "Lev has written ICP and problem hypothesis from onboarding inputs. Founder starts here.",
      closureType: "auto",
    },
    {
      level: 0,
      name: "Validated",
      description:
        "Founder works toward grade target for Customer Problem. DoD is the recommended path; eval iteration tasks drive the grade after each loop.",
      closureType: "soft",
      isTestRung: true,
      closureDoD: {
        venture: CD_R0_VENTURE_DOD,
        bootstrap: CD_R0_BOOTSTRAP_DOD,
      },
      gradeAnchors: [
        { category: "Customer Problem", rubric: "venture", minGrade: "B" },
        {
          category: "Validation speed & signal clarity",
          rubric: "bootstrap",
          minGrade: "B",
        },
      ],
    },
    {
      level: 1,
      name: "Creation",
      description:
        "Creation phase — task model deferred. Bridge advances founders here when R0 grade clears; no tasks generated yet.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Competitive Landscaping
// ---------------------------------------------------------------------------

const COMPETITIVE_LANDSCAPING: Ladder = {
  id: LADDER_IDS.COMPETITIVE_LANDSCAPING,
  name: "Competitive Landscaping",
  stepId: STEP_IDS.COMPETITIVE,
  onboardingCapRung: -1,
  // Bootstrap projects skip Competitive Landscaping entirely (decided
  // 2026-06-14). Niche bootstrap wedges don't engage the venture moat /
  // whitespace analysis; bootstrap seeds this ladder at R1 from day 1 so
  // no tasks generate and no eval fires.
  skipForRubric: ["bootstrap"],
  rungs: [
    {
      level: -1,
      name: "Analyzed",
      description:
        "Lev has listed competitors with offering, pricing, and positioning, and analyzed strengths/weaknesses + customer gaps. Founder starts here.",
      closureType: "auto",
    },
    {
      level: 0,
      name: "Validated",
      description:
        "Founder writes the differentiation story and finds whitespace. DoD is the recommended path; eval drives the grade.",
      closureType: "soft",
      isTestRung: true,
      closureDoD: [
        {
          id: "r0-competitive-landscaping-differentiation-statement",
          signal: "artifact",
          description:
            "Differentiation statement: \"Only X that does Y for Z\" written in doc",
        },
        {
          id: "r0-competitive-landscaping-whitespace-named",
          signal: "artifact",
          description:
            "Whitespace named explicitly: what customer need is structurally underserved by incumbents (regulatory / geographic / segment-size / org-structure reason)? OR what feasible disruption vector exists (new tech / new biz model / new distribution channel)?",
        },
        {
          id: "r0-competitive-landscaping-differentiation-tied-to-whitespace",
          signal: "artifact",
          description:
            "Differentiation tied to the named whitespace, not invented",
        },
        {
          id: "r0-competitive-landscaping-defensibility-analysis",
          signal: "artifact",
          description:
            "Defensibility analysis: moat type (network effects, switching costs, data accumulation, brand, PSL/process advantages) + what specifically blocks copy",
        },
        {
          id: "r0-competitive-landscaping-incumbent-response",
          signal: "artifact",
          description:
            "Incumbent response scenario: what happens if a well-resourced competitor copies, and how you outmaneuver",
        },
      ],
      gradeAnchors: [
        { category: "Competition & Moat", rubric: "venture", minGrade: "B" },
      ],
    },
    {
      level: 1,
      name: "Creation",
      description:
        "Creation phase — task model deferred. Bridge advances founders here when R0 grade clears; no tasks generated yet.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Market Sizing & Pricing
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Market Sizing & Pricing (R0 DoD is rubric-conditional)
// ---------------------------------------------------------------------------

const TAM_R0_VENTURE_DOD: DoDCriterion[] = [
  // Numbers stay for the first loop; eval-driven thereafter.
  {
    id: "r0-market-sizing-pricing-venture-tam-cross-referenced",
    signal: "artifact",
    description: "TAM cross-referenced with ≥2 external sources",
  },
  {
    id: "r0-market-sizing-pricing-venture-quantified-trends",
    signal: "artifact",
    description: "Document quantified trends from different sources",
  },
  {
    id: "r0-market-sizing-pricing-venture-yoy-growth",
    signal: "artifact",
    description: "YoY growth % cited",
  },
  {
    id: "r0-market-sizing-pricing-venture-adjacent-market-sized",
    signal: "artifact",
    description:
      "≥1 adjacent market sized with evidence + total TAM with adjacencies included",
  },
  {
    id: "r0-market-sizing-pricing-venture-pricing-conversations-5",
    signal: "activity",
    description: "≥5 pricing conversations documented with prospect reactions",
  },
  {
    id: "r0-market-sizing-pricing-venture-ltv-cac",
    signal: "artifact",
    description: "LTV:CAC math attempted (assumptions stated)",
  },
  {
    id: "r0-market-sizing-pricing-venture-expansion-vectors",
    signal: "artifact",
    description: "Expansion vectors stated with evidence",
  },
  {
    id: "r0-market-sizing-pricing-venture-revenue-scenario",
    signal: "artifact",
    description:
      "Revenue potential scenario ($X with Y% share in Z years)",
  },
];

const TAM_R0_BOOTSTRAP_DOD: DoDCriterion[] = [
  {
    id: "r0-market-sizing-pricing-bootstrap-arpu-target",
    signal: "artifact",
    description:
      "ARPU target named ($15–$49 band) with business value justification",
  },
  {
    id: "r0-market-sizing-pricing-bootstrap-annual-prepay",
    signal: "artifact",
    description:
      "Annual prepay structure drafted (target ≥20% of subscribers on annual)",
  },
  {
    id: "r0-market-sizing-pricing-bootstrap-search-ad-payback",
    signal: "artifact",
    description:
      "Search-ad payback math (target <1 month using benchmarked CPC)",
  },
  {
    id: "r0-market-sizing-pricing-bootstrap-ltv-calculation",
    signal: "artifact",
    description:
      "LTV calculation (target ≥$200) with churn assumption stated",
  },
  {
    id: "r0-market-sizing-pricing-bootstrap-mrr-scenario",
    signal: "artifact",
    description:
      "$5k MRR scenario: paying-user count (target 100–350) + funnel from cold search",
  },
];

const MARKET_SIZING_PRICING: Ladder = {
  id: LADDER_IDS.MARKET_SIZING_PRICING,
  name: "Market Sizing & Pricing",
  stepId: STEP_IDS.TAM,
  onboardingCapRung: -1,
  rungs: [
    {
      level: -1,
      name: "Structured",
      description:
        "Lev has TAM/SAM/SOM with named methodology and a draft pricing model. Founder starts here.",
      closureType: "auto",
    },
    {
      level: 0,
      name: "Validated",
      description:
        "Founder cross-validates market numbers and tests pricing with real prospects. DoD numbers below apply to the first loop only — subsequent loops are eval-driven.",
      closureType: "soft",
      isTestRung: true,
      closureDoD: {
        venture: TAM_R0_VENTURE_DOD,
        bootstrap: TAM_R0_BOOTSTRAP_DOD,
      },
      // Multi-anchor: BOTH categories must clear B before this ladder
      // graduates. Per the LEV-947 PR3 update, the grade target is B (was A).
      gradeAnchors: [
        { category: "TAM & Timing", rubric: "venture", minGrade: "B" },
        { category: "Business Model", rubric: "venture", minGrade: "B" },
        {
          category: "Unit economics & profitability",
          rubric: "bootstrap",
          minGrade: "B",
        },
      ],
    },
    {
      level: 1,
      name: "Creation",
      description:
        "Creation phase — task model deferred. Bridge advances founders here when both R0 anchors clear B; no tasks generated yet.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Brand Development (maps to step POSITIONING)
// ---------------------------------------------------------------------------

const BRAND_DEVELOPMENT: Ladder = {
  id: LADDER_IDS.BRAND_DEVELOPMENT,
  name: "Brand Development",
  stepId: STEP_IDS.BRAND,
  onboardingCapRung: -1,
  rungs: [
    {
      level: -1,
      name: "Branding Kit",
      description:
        "Lev has name, tagline, POV, core messaging, and placeholder visual identity. Founder starts here.",
      closureType: "auto",
    },
    {
      level: 0,
      name: "Validated",
      description:
        "Founder applies the brand across surfaces, captures audience reactions, and iterates based on feedback. No eval rubric covers brand — closure is LLM-judged.",
      closureType: "soft",
      isTestRung: true,
      // No gradeAnchors — Brand has no rubric category. Closure is LLM-judged
      // on "audience comprehension + recall sufficient."
      closureDoD: [
        {
          id: "r0-brand-development-landing-page-live",
          signal: "artifact",
          description: "Landing page or website live",
        },
        {
          id: "r0-brand-development-brand-applied-surfaces",
          signal: "artifact",
          description:
            "Brand applied across ≥2 surfaces with consistent visual identity",
        },
        {
          id: "r0-brand-development-feedback-insights-5",
          signal: "insight",
          description:
            "≥5 brand-feedback insights captured (showed to real people)",
        },
        {
          id: "r0-brand-development-recall-comprehension-signal",
          signal: "mixed",
          description:
            "Recall and comprehension signal documented (do they remember? do they get it?)",
        },
        {
          id: "r0-brand-development-keep-change-decisions",
          signal: "artifact",
          description: "Keep/change decisions documented",
        },
        {
          id: "r0-brand-development-final-iteration",
          signal: "artifact",
          description: "Final brand iteration captured",
        },
        {
          id: "r0-brand-development-llm-judge-comprehension",
          signal: "mixed",
          description:
            "LLM judges audience comprehension + recall as sufficient",
        },
      ],
    },
    {
      level: 1,
      name: "Creation",
      description:
        "Creation phase — task model deferred. Bridge advances founders here when LLM judges R0 closure; no tasks generated yet.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Go To Market
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Go To Market (R0 DoD is rubric-conditional)
// ---------------------------------------------------------------------------

const GTM_R0_VENTURE_DOD: DoDCriterion[] = [
  {
    id: "r0-go-to-market-venture-beachhead-refined",
    signal: "artifact",
    description:
      "Beachhead refined (take current ICP and get more specific about first target / beachhead)",
  },
  {
    id: "r0-go-to-market-venture-icp-queryable",
    signal: "artifact",
    description:
      "ICP refined to queryable form (a list you could actually buy)",
  },
  {
    id: "r0-go-to-market-venture-channel-experiments",
    signal: "activity",
    description:
      "Channel experiments documented (hypothesis + test + result) with quantitative metrics",
  },
  {
    id: "r0-go-to-market-venture-winning-channels-cac",
    signal: "mixed",
    description:
      "Select ≥1 winning channels and outline CAC",
  },
  {
    id: "r0-go-to-market-venture-growth-loop",
    signal: "artifact",
    description: "Growth loop testing and justifying",
  },
  {
    id: "r0-go-to-market-venture-penetration-scenario",
    signal: "artifact",
    description:
      "Penetration scenario: path to 10%+ of SOM in 5y articulated with channel mix",
  },
];

const GTM_R0_BOOTSTRAP_DOD: DoDCriterion[] = [
  {
    id: "r0-go-to-market-bootstrap-keyword-family-named",
    signal: "artifact",
    description:
      "Keyword family named (narrow, exact-match intent — e.g. \"generate redirects from CSV\", \"sitemap validator\")",
  },
  {
    id: "r0-go-to-market-bootstrap-search-volume-verified",
    signal: "artifact",
    description:
      "Search volume verified: 500–10k exact-match searches/month for the chosen keyword family",
  },
  {
    id: "r0-go-to-market-bootstrap-cpc-measured",
    signal: "mixed",
    description:
      "CPC measured via test campaign (target <$3 across the keyword family)",
  },
  {
    id: "r0-go-to-market-bootstrap-landing-page-keyword-intent",
    signal: "artifact",
    description:
      "Landing page mirrors keyword intent (≥1 LP wired to demo with copy matching the search query)",
  },
  {
    id: "r0-go-to-market-bootstrap-cold-click-cvr",
    signal: "mixed",
    description:
      "Cold-click trial CVR benchmarked (target ≥10% from paid search traffic alone)",
  },
];

const GO_TO_MARKET: Ladder = {
  id: LADDER_IDS.GO_TO_MARKET,
  name: "Go To Market",
  stepId: STEP_IDS.GTM,
  onboardingCapRung: -1,
  rungs: [
    {
      level: -1,
      name: "Channel Hypothesis",
      description:
        "Lev has channel hypotheses + motion type + launch sequence sketched. Founder starts here.",
      closureType: "auto",
    },
    {
      level: 0,
      name: "Validated",
      description:
        "Founder refines beachhead + ICP, runs channel experiments, tests growth loops. DoD describes the recommended path; eval drives the grade. Counts stripped — the loop runs until eval criteria are reached.",
      closureType: "soft",
      isTestRung: true,
      closureDoD: {
        venture: GTM_R0_VENTURE_DOD,
        bootstrap: GTM_R0_BOOTSTRAP_DOD,
      },
      gradeAnchors: [
        { category: "Go-To-Market", rubric: "venture", minGrade: "A" },
        {
          category: "Scalable customer acquisition",
          rubric: "bootstrap",
          minGrade: "B",
        },
      ],
    },
    {
      level: 1,
      name: "Creation",
      description:
        "Creation phase — task model deferred. Bridge advances founders here when R0 grade clears; no tasks generated yet.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Product Build (R0 DoD is rubric-conditional)
// ---------------------------------------------------------------------------

const PB_R0_VENTURE_DOD: DoDCriterion[] = [
  {
    id: "r0-product-build-venture-prd-spec",
    signal: "artifact",
    description: "PRD/spec exists with MVP scope explicit, features prioritized",
  },
  {
    id: "r0-product-build-venture-wireframes",
    signal: "artifact",
    description: "Wireframes for key flows",
  },
  {
    id: "r0-product-build-venture-time-to-build-estimate",
    signal: "artifact",
    description: "Time-to-build estimate: months + named dependencies",
  },
  {
    id: "r0-product-build-venture-tech-investment-estimate",
    signal: "artifact",
    description:
      "Tech investment estimate: dollar figure + assumptions, OR adoption-driven justification if no $ claimed",
  },
  {
    id: "r0-product-build-venture-tech-stack-feasibility",
    signal: "artifact",
    description:
      "Tech stack feasibility analysis: off-the-shelf vs. bespoke infra; risks named",
  },
  {
    id: "r0-product-build-venture-operational-risk",
    signal: "artifact",
    description:
      "Operational risk: pre-PMF ops burden (moderation, content review, support, partner SLAs)",
  },
  {
    id: "r0-product-build-venture-legal-regulatory",
    signal: "artifact",
    description:
      "Legal/regulatory considerations: risks named even if minor",
  },
  {
    id: "r0-product-build-venture-scalability-path",
    signal: "artifact",
    description:
      "Scalability path: what changes at 10x (infra, ops, team)",
  },
];

const PB_R0_BOOTSTRAP_DOD: DoDCriterion[] = [
  {
    id: "r0-product-build-bootstrap-prd-spec",
    signal: "artifact",
    description:
      "PRD/spec exists with MVP scope explicit, features prioritized; standard templates targeted (Next.js + Supabase + Stripe pattern)",
  },
  {
    id: "r0-product-build-bootstrap-wireframes",
    signal: "artifact",
    description: "Wireframes for key flows + onboarding flow specifically",
  },
  {
    id: "r0-product-build-bootstrap-time-to-build",
    signal: "artifact",
    description:
      "Time-to-build estimate: 3–14 days achievable with standard templates; bespoke infra avoided",
  },
  {
    id: "r0-product-build-bootstrap-onboarding-flow-design",
    signal: "artifact",
    description:
      "Onboarding flow design: 3-step onboarding sketched (sign up → sample data → immediate value); first-value target <30 min; self-serve billing planned",
  },
  {
    id: "r0-product-build-bootstrap-ops-design",
    signal: "artifact",
    description:
      "Ops design: monthly ops burden estimated; <4 hrs/month achievable; no required human-in-loop features; scripted maintenance only",
  },
  {
    id: "r0-product-build-bootstrap-tech-stack",
    signal: "artifact",
    description:
      "Tech stack: serverless-first; deterministic logic; 3–7 DB tables; one-click deploy",
  },
  {
    id: "r0-product-build-bootstrap-legal-regulatory",
    signal: "artifact",
    description: "Legal/regulatory considerations: risks named",
  },
];

const PRODUCT_BUILD: Ladder = {
  id: LADDER_IDS.PRODUCT_BUILD,
  name: "Product Build",
  stepId: STEP_IDS.PRODUCT_BUILD,
  onboardingCapRung: -1,
  rungs: [
    {
      level: -1,
      name: "Idea Only",
      description:
        "Lev has the initial idea sketched. Founder starts here.",
      closureType: "auto",
    },
    {
      level: 0,
      name: "Validated",
      description:
        "Founder strengthens the idea into a buildable PRD. Under the new R−1 → R0 → R1 model PB behaves like every other ladder: eval reads feasibility; founder works toward grade target.",
      closureType: "soft",
      isTestRung: true,
      // Rubric-conditional DoD (Option A): venture and bootstrap variants
      // differ. Consumers MUST resolve the variant for the project's
      // rubricType before reading.
      closureDoD: {
        venture: PB_R0_VENTURE_DOD,
        bootstrap: PB_R0_BOOTSTRAP_DOD,
      },
      // Both venture and bootstrap anchors live on this rung. Eval-bridge
      // filters by `Project.rubricType` to pick the applicable subset.
      // Venture: target A (sharpened in PR3 from PR1's B-gate framing).
      // Bootstrap: multi-anchor at B (Speed of product build + Easy
      // onboarding) — both must clear B before the ladder graduates.
      gradeAnchors: [
        { category: "Product Feasibility", rubric: "venture", minGrade: "A" },
        {
          category: "Speed of product build (prototype feasibility)",
          rubric: "bootstrap",
          minGrade: "B",
        },
        { category: "Easy onboarding", rubric: "bootstrap", minGrade: "B" },
      ],
    },
    {
      level: 1,
      name: "Creation",
      description:
        "Creation phase — the previous R3-R5 construction work (Building / Testing / Iterating) lives here under the new collapse. Task model deferred. Bridge advances founders here when R0 grade clears; no tasks generated yet.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------

const TEAM: Ladder = {
  id: LADDER_IDS.TEAM,
  name: "Team",
  stepId: STEP_IDS.TEAM,
  onboardingCapRung: -1,
  // Bootstrap projects skip Team entirely (decided 2026-06-14). Solo-dev
  // bootstrap MVPs don't require formal team formation pre-PMF; bootstrap
  // seeds this ladder at R1 from day 1.
  skipForRubric: ["bootstrap"],
  rungs: [
    {
      level: -1,
      name: "Gaps Identified",
      description:
        "Lev has required roles listed with rationale, founder skill audit, and priority hire order. Founder starts here.",
      closureType: "auto",
    },
    {
      level: 0,
      name: "Validated",
      description:
        "Founder runs the hiring process and closes the founding team. Eval explicitly excludes team — closure is hard on the founding-team signed signal.",
      closureType: "hard",
      isTestRung: true,
      // No gradeAnchors — eval excludes team. Closure is hard on the
      // "founding team signed" artifact.
      closureDoD: [
        {
          id: "r0-team-candidate-conversations-5",
          signal: "activity",
          description: "≥5 candidate conversations documented",
        },
        {
          id: "r0-team-hiring-pipeline-started",
          signal: "artifact",
          description:
            "Hiring pipeline started (sourcing, intros, applications)",
        },
        {
          id: "r0-team-equity-comp-framework",
          signal: "artifact",
          description: "Equity/comp framework drafted",
        },
        {
          id: "r0-team-deep-mutual-interest-candidate",
          signal: "activity",
          description: "≥1 candidate in deep mutual-interest stage",
        },
        {
          id: "r0-team-trial-work-completed",
          signal: "activity",
          description:
            "Trial work or extended interview completed with ≥1 candidate",
        },
        {
          id: "r0-team-offer-terms-decided",
          signal: "artifact",
          description: "Offer terms decided (equity %, role, comp)",
        },
        {
          id: "r0-team-founding-team-signed",
          signal: "artifact",
          description: "Founding team signed (offers accepted)",
        },
        {
          id: "r0-team-roles-responsibilities",
          signal: "artifact",
          description: "Roles and responsibilities documented",
        },
        {
          id: "r0-team-equity-comp-finalized",
          signal: "artifact",
          description: "Equity/comp finalized (founding agreement)",
        },
      ],
    },
    {
      level: 1,
      name: "Creation",
      description:
        "Creation phase — task model deferred. Bridge advances founders here when team signs; no tasks generated yet.",
    },
  ],
};

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const LADDERS: Record<LadderId, Ladder> = {
  [LADDER_IDS.CUSTOMER_DISCOVERY]: CUSTOMER_DISCOVERY,
  [LADDER_IDS.COMPETITIVE_LANDSCAPING]: COMPETITIVE_LANDSCAPING,
  [LADDER_IDS.MARKET_SIZING_PRICING]: MARKET_SIZING_PRICING,
  [LADDER_IDS.BRAND_DEVELOPMENT]: BRAND_DEVELOPMENT,
  [LADDER_IDS.GO_TO_MARKET]: GO_TO_MARKET,
  [LADDER_IDS.PRODUCT_BUILD]: PRODUCT_BUILD,
  [LADDER_IDS.TEAM]: TEAM,
};

export const ALL_LADDERS: ReadonlyArray<Ladder> = Object.values(LADDERS);

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function getLadder(id: LadderId): Ladder {
  return LADDERS[id];
}

export function getLadderByStepId(stepId: StepId): Ladder | undefined {
  return ALL_LADDERS.find((l) => l.stepId === stepId);
}

export function getRung(ladderId: LadderId, level: number): Rung | undefined {
  return LADDERS[ladderId].rungs.find((r) => r.level === level);
}

export function getMaxRungLevel(ladderId: LadderId): number {
  const rungs = LADDERS[ladderId].rungs;
  return rungs[rungs.length - 1]!.level;
}

export function isTerminalRung(ladderId: LadderId, level: number): boolean {
  return level === getMaxRungLevel(ladderId);
}

// ---------------------------------------------------------------------------
// Rubric-conditional DoD helper
// ---------------------------------------------------------------------------

/**
 * Resolve a rung's `closureDoD` to a flat `DoDCriterion[]` for a given rubric
 * variant.
 *
 * Most ladders carry a flat-array `closureDoD`; PB R0 carries a rubric-conditional
 * map (`Partial<Record<RubricType, DoDCriterion[]>>`) since venture and bootstrap
 * variants differ. This helper transparently handles both shapes:
 *
 * - Flat array → returned as-is.
 * - Conditional map → returns the variant matching `rubricType`. Falls back to
 *   `venture` if the requested variant is missing (so the bridge never gets a
 *   bare `undefined`). If both variants are missing, returns `[]`.
 * - Missing `closureDoD` → empty array.
 */
export function resolveRungClosureDoD(
  rung: Rung,
  rubricType?: RubricType,
): DoDCriterion[] {
  const dod = rung.closureDoD;
  if (!dod) return [];
  if (Array.isArray(dod)) return dod;

  // Rubric-conditional map. Pick the variant; fall back to venture.
  const variant = rubricType ?? "venture";
  return dod[variant] ?? dod.venture ?? [];
}

/**
 * Whether a ladder is fully skipped under the given rubric. Skipped ladders
 * are seeded one rung past R0 (R1 Creation) so no tasks generate, no eval
 * fires, and the ladder is effectively graduated from day 1. Returns `false`
 * when `rubricType` is omitted (default = venture, which never skips).
 */
export function isLadderSkippedForRubric(
  ladder: Ladder,
  rubricType?: RubricType,
): boolean {
  if (!rubricType) return false;
  return ladder.skipForRubric?.includes(rubricType) ?? false;
}
